import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Button } from 'react-bootstrap';
import api from '../api';
import './AdminUserDetailsPage.css';

const AdminUserDetailsPage = () => {
    const { userId } = useParams();
    const navigate = useNavigate();

    const [user, setUser] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    // Address form states
    const [address1, setAddress1] = useState('');
    const [address2, setAddress2] = useState('');
    const [city, setCity] = useState('');
    const [stateVal, setStateVal] = useState('');
    const [zip, setZip] = useState('');
    const [updateMsg, setUpdateMsg] = useState('');

    // Email form state
    const [email, setEmail] = useState('');
    const [updateMsgEmail, setUpdateMsgEmail] = useState('');

    // For displaying the tax category's name
    const [taxCategories, setTaxCategories] = useState([]);

    // User bookings
    const [userBookings, setUserBookings] = useState([]);

    //Preauth Policies
    const [preauthHoldPolicy, setPreauthHoldPolicy] = useState('AUTO');
    const [updateMsgPreauth, setUpdateMsgPreauth] = useState('');

    useEffect(() => {
        const fetchUserDetails = async () => {
            try {
                const userResp = await api.get(`/users/${userId}`);
                setUser(userResp.data);

                setAddress1(userResp.data.address1 || '');
                setAddress2(userResp.data.address2 || '');
                setCity(userResp.data.city || '');
                setStateVal(userResp.data.state || '');
                setZip(userResp.data.zip || '');
                setEmail(userResp.data.email || '');
                setPreauthHoldPolicy(userResp.data.preauthHoldPolicy || 'AUTO');

                fetchUserBookings(userResp.data.customerId);
            } catch (err) {
                console.error('Failed to fetch user details:', err);
                setError('Failed to load user details');
            } finally {
                setLoading(false);
            }
        };

        const fetchTaxCategories = async () => {
            try {
                const resp = await api.get('/lightspeed/taxes');
                setTaxCategories(resp.data);
            } catch (err) {
                console.error('Failed to load tax categories:', err);
            }
        };

        const fetchUserBookings = async (customerId) => {
            if (!customerId) {
                setUserBookings([]);
                return;
            }

            try {
                const resp = await api.get(`/admin/bookings/user/${customerId}`);

                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const upcoming = (resp.data || []).filter((booking) => {
                    if (booking.cancelled) return false;
                    if (!booking.endDate) return false;

                    const bookingEnd = new Date(booking.endDate);
                    return bookingEnd >= today;
                });

                upcoming.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
                setUserBookings(upcoming);
            } catch (err) {
                console.error('Failed to fetch user bookings:', err);
            }
        };

        fetchUserDetails();
        fetchTaxCategories();
    }, [userId]);

    if (loading) return <p>Loading user details...</p>;
    if (error) return <p>{error}</p>;
    if (!user) return <p>User not found</p>;

    let assignedTaxName = 'None';
    if (user.assignedTaxCategoryId) {
        const found = taxCategories.find(
            (tc) => tc.taxCategoryID === user.assignedTaxCategoryId
        );
        assignedTaxName = found ? found.tax1Name : `ID#${user.assignedTaxCategoryId}`;
    }

    const handleSubmitAddress = async (e) => {
        e.preventDefault();
        setUpdateMsg('');

        try {
            const response = await api.put(`/users/${userId}/address`, {
                address1,
                address2,
                city,
                state: stateVal,
                zip
            });
            setUpdateMsg(response.data);

            const refetch = await api.get(`/users/${userId}`);
            setUser(refetch.data);
        } catch (err) {
            console.error('Failed to update address:', err);
            if (err.response && err.response.data) {
                setUpdateMsg(err.response.data);
            } else {
                setUpdateMsg('Failed to update address');
            }
        }
    };

    const handleSubmitEmail = async (e) => {
        e.preventDefault();
        setUpdateMsgEmail('');

        try {
            const response = await api.put(`/users/${userId}/email`, {
                email
            });
            setUpdateMsgEmail(response.data);

            const refetch = await api.get(`/users/${userId}`);
            setUser(refetch.data);
        } catch (err) {
            console.error('Failed to update email:', err);
            if (err.response && err.response.data) {
                setUpdateMsgEmail(err.response.data);
            } else {
                setUpdateMsgEmail('Failed to update email');
            }
        }
    };

    const handleSubmitPreauthPolicy = async (e) => {
        e.preventDefault();
        setUpdateMsgPreauth('');

        try {
            const response = await api.put(`/users/${userId}/preauth-hold-policy`, {
                preauthHoldPolicy
            });

            setUpdateMsgPreauth(response.data.message || 'Preauthorization hold policy updated.');

            const refetch = await api.get(`/users/${userId}`);
            setUser(refetch.data);
        } catch (err) {
            console.error('Failed to update preauthorization hold policy:', err);

            if (err.response && err.response.data) {
                if (typeof err.response.data === 'string') {
                    setUpdateMsgPreauth(err.response.data);
                } else {
                    setUpdateMsgPreauth(err.response.data.message || 'Failed to update preauthorization hold policy.');
                }
            } else {
                setUpdateMsgPreauth('Failed to update preauthorization hold policy.');
            }
        }
    };

    return (
        <div className="admin-user-details-container">
            <h2>User Details (Admin View)</h2>

            <div className="user-info-section">
                <p><strong>Customer ID:</strong> {user.customerId}</p>
                <p><strong>Company:</strong> {user.company}</p>
                <p><strong>Name:</strong> {user.firstName} {user.lastName}</p>
                <p><strong>Email:</strong> {user.email}</p>
                <p><strong>Username:</strong> {user.username}</p>
                <p><strong>Mobile:</strong> {user.phone}</p>
                <p><strong>Work Phone:</strong> {user.workPhone}</p>
                <p><strong>Assigned Tax Category:</strong> {assignedTaxName}</p>
            </div>

            <div className="preauth-section">
                <h3>Preauthorization Hold</h3>

                <p>
                    <strong>Current Policy:</strong> {user.preauthHoldPolicy || 'AUTO'}
                </p>

                {user.effectivePreauthHoldRequired !== undefined && (
                    <p>
                        <strong>Effective Result:</strong>{' '}
                        {user.effectivePreauthHoldRequired ? 'Hold Required' : 'Hold Waived'}
                    </p>
                )}

                <Form onSubmit={handleSubmitPreauthPolicy} className="preauth-form">
                    <Form.Group className="mb-2">
                        <Form.Label>Preauthorization Hold Policy</Form.Label>
                        <Form.Select
                            value={preauthHoldPolicy}
                            onChange={(e) => setPreauthHoldPolicy(e.target.value)}
                        >
                            <option value="AUTO">
                                Auto - Preferred discount waives hold; otherwise hold required
                            </option>
                            <option value="REQUIRED">
                                Require Hold - Override discount/trust status
                            </option>
                            <option value="WAIVED">
                                Waive Hold - Trusted customer override
                            </option>
                        </Form.Select>
                    </Form.Group>

                    <Button variant="primary" type="submit" className="submit-preauth-btn">
                        Update Preauthorization Policy
                    </Button>
                </Form>

                {updateMsgPreauth && <p className="update-message">{updateMsgPreauth}</p>}
            </div>

            <div className="user-bookings-section">
                <h3>Upcoming / Current Bookings</h3>

                {userBookings.length === 0 ? (
                    <p>No upcoming bookings found.</p>
                ) : (
                    <div className="bookings-list">
                        {userBookings.map((booking) => (
                            <div key={booking.id} className="booking-card">
                                <p>
                                    <strong>Item:</strong>{' '}
                                    {booking.item?.description || `Item #${booking.item?.itemId || 'Unknown'}`}
                                </p>

                                {booking.item?.customSku && (
                                    <p><strong>SKU:</strong> {booking.item.customSku}</p>
                                )}

                                <p><strong>Start Date:</strong> {booking.startDate}</p>
                                <p><strong>End Date:</strong> {booking.endDate}</p>
                                <p><strong>Rental Days:</strong> {booking.rentalDays ?? 'N/A'}</p>
                                <p><strong>Total Price:</strong> ${Number(booking.totalPrice || 0).toFixed(2)}</p>
                                <p><strong>Amount Paid:</strong> ${Number(booking.amountPaid || 0).toFixed(2)}</p>
                                <p><strong>Payment Method:</strong> {booking.paymentMethod || 'N/A'}</p>
                                <p><strong>Insurance:</strong> {booking.insurance ? 'Yes' : 'No'}</p>

                                {booking.nrFeeApplied != null && booking.nrFeeApplied > 0 && (
                                    <p>
                                        <strong>Reservation Deposit Applied:</strong>{' '}
                                        ${Number(booking.nrFeeApplied).toFixed(2)}
                                    </p>
                                )}

                                {booking.workorderId && (
                                    <p><strong>Workorder ID:</strong> {booking.workorderId}</p>
                                )}

                                {booking.bookingGroup && (
                                    <p><strong>Booking Group:</strong> {booking.bookingGroup}</p>
                                )}

                                {booking.paypalTransactionId && (
                                    <p><strong>PayPal Transaction ID:</strong> {booking.paypalTransactionId}</p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="email-section">
                <h3>Update Email</h3>
                <Form onSubmit={handleSubmitEmail} className="email-form">
                    <Form.Group className="mb-2">
                        <Form.Label>Email Address</Form.Label>
                        <Form.Control
                            type="text"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </Form.Group>
                    <Button variant="primary" type="submit" className="submit-email-btn">
                        Update Email
                    </Button>
                </Form>
                {updateMsgEmail && <p className="update-message">{updateMsgEmail}</p>}
            </div>

            <div className="address-section">
                <h3>Update Address</h3>
                <Form onSubmit={handleSubmitAddress} className="address-form">
                    <Form.Group className="mb-2">
                        <Form.Label>Address 1</Form.Label>
                        <Form.Control
                            type="text"
                            value={address1}
                            onChange={(e) => setAddress1(e.target.value)}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Label>Address 2</Form.Label>
                        <Form.Control
                            type="text"
                            value={address2}
                            onChange={(e) => setAddress2(e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Label>City</Form.Label>
                        <Form.Control
                            type="text"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Label>State</Form.Label>
                        <Form.Control
                            type="text"
                            value={stateVal}
                            onChange={(e) => setStateVal(e.target.value)}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Label>Zip</Form.Label>
                        <Form.Control
                            type="text"
                            value={zip}
                            onChange={(e) => setZip(e.target.value)}
                            required
                        />
                    </Form.Group>

                    <Button variant="primary" type="submit" className="submit-address-btn">
                        Update Address
                    </Button>
                </Form>
                {updateMsg && <p className="update-message">{updateMsg}</p>}
            </div>

            <Button variant="secondary" onClick={() => navigate('/admin/manage-users')}>
                Back to Manage Users
            </Button>
        </div>
    );
};

export default AdminUserDetailsPage;